// Paket 75 (Geschenke): einmaliger Hinweis zu einem aktivierten Geschenk.
//
// Aktiviert wird serverseitig beim ersten authentifizierten Kontakt — auch über
// eine alte App-Version. Dieser Host zeigt danach den Hinweis je Geschenk genau
// einmal (Text je Anlass) und meldet ihn als gesehen. Bestandsschutz behält
// sein eigenes Popup (BestandsschutzHost); solange das offen ist, wartet dieser
// Host, damit nie zwei Dialoge übereinander liegen.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { acknowledgeGiftPopup, getGiftPopups, type GiftPopup } from '../api/gifts';
import { getBestandsschutz } from '../api/membership';
import { queryKeys } from '../api/queryKeys';
import { useAuth } from '../auth/AuthProvider';
import { isGuestUser } from '../auth/guest';
import { Button, Icon, ResponsiveDialog } from '../components/ui';
import { useT } from '../i18n';
import { useStartupGate } from './StartupGateCoordinator';

export const GIFT_POPUPS_QUERY_KEY = ['gifts', 'popups'] as const;

function formatDate(lang: string, iso: string | null): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(lang, { dateStyle: 'long' }).format(parsed);
}

export function giftPopupText(
  gift: GiftPopup,
  lang: string,
  t: (key: string, vars?: Record<string, string | number>) => string,
): { title: string; body: string } {
  const date = formatDate(lang, gift.endsAt);
  const tier = t(`tier.${gift.tier}`);
  if (gift.reason === 'tester_dank') {
    return { title: t('gift.popup.tester_dank.title'), body: t('gift.popup.tester_dank.body') };
  }
  if (gift.reason === 'coin_dank') {
    return { title: t('gift.popup.coin_dank.title'), body: t('gift.popup.coin_dank.body', { date }) };
  }
  return {
    title: t('gift.popup.generic.title'),
    body: gift.endsAt
      ? t('gift.popup.generic.body_until', { tier, date })
      : t('gift.popup.generic.body_unlimited', { tier }),
  };
}

export function GiftHost() {
  const auth = useAuth();
  const gate = useStartupGate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lang, t } = useT();
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState<number[]>([]);
  const enabled = auth.status === 'authed' && !isGuestUser(auth.user ?? null);

  const popups = useQuery({
    queryKey: [...GIFT_POPUPS_QUERY_KEY, auth.user?.userId ?? null],
    queryFn: getGiftPopups,
    enabled,
    staleTime: 5 * 60_000,
    retry: 0,
  });
  // Gleicher Schlüssel wie im PromoProvider — ein Abruf für beide.
  const bestandsschutz = useQuery({
    queryKey: ['bestandsschutz'],
    queryFn: getBestandsschutz,
    enabled,
    staleTime: 60_000,
  });

  const current = (popups.data ?? []).find((gift) => !handled.includes(gift.id)) ?? null;
  if (!enabled || !current || gate !== 'none') return null;
  if (bestandsschutz.isPending || bestandsschutz.data?.state === 'pending') return null;

  const { title, body } = giftPopupText(current, lang, t);
  const offerFounders = current.reason === 'tester_dank';

  const close = async (toFounders: boolean) => {
    setBusy(true);
    try {
      await acknowledgeGiftPopup(current.id);
    } catch {
      // Nicht bestätigt → der Hinweis kommt beim nächsten Start erneut.
    } finally {
      setBusy(false);
      setHandled((ids) => [...ids, current.id]);
      void queryClient.invalidateQueries({ queryKey: queryKeys.membership.snapshot() });
      if (toFounders) navigate('/mitgliedschaft/uebersicht');
    }
  };

  return (
    <ResponsiveDialog
      open
      onClose={() => { void close(false); }}
      title={title}
      footer={(
        <>
          <Button variant={offerFounders ? 'secondary' : 'primary'} loading={busy && !offerFounders} disabled={busy} onClick={() => void close(false)}>
            {t('gift.popup.continue')}
          </Button>
          {offerFounders ? (
            <Button loading={busy} disabled={busy} onClick={() => void close(true)}>
              {t('membership.overview.open_overview')}
            </Button>
          ) : null}
        </>
      )}
    >
      <p>
        <Icon name="gift" size={20} />
        {' '}
        {body}
      </p>
    </ResponsiveDialog>
  );
}
